import React, { useState, useEffect, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { getActionTracker, saveActionTracker } from "shared/api/function_data";
import { invalidateActionTracker } from "shared/lib/queryClient";
import "./ActionTracker.css";

const STATUS_OPTIONS = [
  "All",
  "Active",
  "Open",
  "In Progress",
  "Completed",
  "Overdue",
  "Blocked",
];

export default function ActionTracker({
  theme,
  functionId,
  week = "N/A",
  year = "N/A",
  userLogin,
  functionConfig,
}) {
  // ─── Derive function (sidebar) options for the dropdown ───
  // The backend returns function_sidebar for a single week already, so its
  // shape is { section_a: { name, items: [] }, section_b: {...}, ... }.
  // We flatten every section's items into a de-duplicated list of { key, label }.
  const functionOptions = React.useMemo(() => {
    const sidebar = functionConfig?.function_sidebar || {};
    const seen = new Set();
    const options = [];
    Object.values(sidebar).forEach((section) => {
      (section?.items || []).forEach((item) => {
        // Only "report" type items represent functions/decks to associate.
        if (item?.type === "report" && item?.label && !seen.has(item.label)) {
          seen.add(item.label);
          options.push({ key: item.key, label: item.label });
        }
      });
    });
    return options;
  }, [functionConfig]);
  const queryClient = useQueryClient();
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("Active");
  const [expandedRow, setExpandedRow] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [newCommentText, setNewCommentText] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [modalForm, setModalForm] = useState({});
  const [editingDetailId, setEditingDetailId] = useState(null);
  const [detailForm, setDetailForm] = useState({});

  // ─── Helpers (defined early for use in filtering/stats) ───

  // Compute display status: if ECD is past and action is not completed, show "Overdue"
  const getDisplayStatus = (action) => {
    if (action.status === "Completed") return "Completed";
    if (action.ecd) {
      const ecdDate = new Date(action.ecd);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (ecdDate < today) {
        return "Overdue";
      }
    }
    return action.status;
  };

  // Check if action is overdue (for highlighting)
  const isOverdue = (action) => {
    return getDisplayStatus(action) === "Overdue";
  };

  // Extract the short, human-friendly number from an action id.
  // IDs look like "ACT-076-1788195258824" → 76. Falls back gracefully.
  const getShortId = (id) => {
    if (!id) return "—";
    const parts = String(id).split("-");
    if (parts.length >= 2 && /^\d+$/.test(parts[1])) {
      return String(parseInt(parts[1], 10));
    }
    return id;
  };

  const getStatusClass = (s) => {
    const map = {
      Open: "at-status--open",
      "In Progress": "at-status--in-progress",
      Completed: "at-status--completed",
      Overdue: "at-status--overdue",
      Blocked: "at-status--blocked",
    };
    return map[s] || "";
  };

  // ─── Load data from API ───
  const loadActions = useCallback(async () => {
    if (!functionId || year === "N/A") return;
    try {
      setLoading(true);
      const data = await getActionTracker(functionId, year);
      setActions(data.actions || []);
    } catch (error) {
      console.error("Failed to load actions:", error);
    } finally {
      setLoading(false);
    }
  }, [functionId, year]);

  useEffect(() => {
    loadActions();
  }, [loadActions]);

  // ─── Save data to API (debounced to avoid request flood) ───
  const saveTimeoutRef = React.useRef(null);
  const persistActions = useCallback(
    (updatedActions) => {
      if (!functionId || year === "N/A") return;
      // Clear any pending save
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
      setSaving(true);
      // Debounce: wait 800ms after last change before saving
      saveTimeoutRef.current = setTimeout(async () => {
        try {
          await saveActionTracker(functionId, year, updatedActions);
          // Keep any other cached consumer of this action tracker fresh.
          // This component keeps its own local state as the editing source of
          // truth, so it is unaffected by the invalidation.
          invalidateActionTracker(queryClient, functionId, year);
        } catch (error) {
          console.error("Failed to save actions:", error);
        } finally {
          setSaving(false);
        }
      }, 800);
    },
    [functionId, year, queryClient],
  );

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
  }, []);

  const updateActions = useCallback(
    (updater) => {
      setActions((prev) => {
        const updated = typeof updater === "function" ? updater(prev) : updater;
        persistActions(updated);
        return updated;
      });
    },
    [persistActions],
  );

  // ─── Filtering (uses getDisplayStatus for computed overdue) ───
  const filteredActions = actions.filter((action) => {
    const term = searchTerm.toLowerCase();
    const matchesSearch =
      action.title.toLowerCase().includes(term) ||
      action.id.toLowerCase().includes(term) ||
      getShortId(action.id).toLowerCase().includes(term) ||
      (action.function_name || "").toLowerCase().includes(term) ||
      action.owner.toLowerCase().includes(term) ||
      (action.action_item || "").toLowerCase().includes(term) ||
      (action.slide_name || "").toLowerCase().includes(term) ||
      (action.line_item || "").toLowerCase().includes(term);
    const computedStatus = getDisplayStatus(action);
    const matchesStatus =
      statusFilter === "All" ||
      (statusFilter === "Active" && computedStatus !== "Completed") ||
      computedStatus === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // ─── Stats (uses getDisplayStatus for accurate overdue count) ───
  const stats = {
    total: actions.length,
    open: actions.filter((a) => getDisplayStatus(a) === "Open").length,
    inProgress: actions.filter((a) => getDisplayStatus(a) === "In Progress").length,
    completed: actions.filter((a) => getDisplayStatus(a) === "Completed").length,
    overdue: actions.filter((a) => getDisplayStatus(a) === "Overdue").length,
    blocked: actions.filter((a) => getDisplayStatus(a) === "Blocked").length,
  };

  // ─── Export CSV by status ───
  const exportByStatus = (status) => {
    const items =
      status === "All"
        ? actions
        : actions.filter((a) => getDisplayStatus(a) === status);

    if (items.length === 0) return;

    const headers = [
      "ID",
      "Title",
      "Action Item",
      "Slide Name",
      "Line Item",
      "Action Type",
      "Function",
      "Owner",
      "Status",
      "ECD",
      "ECD History",
      "Week",
      "Created Date",
      "Created By",
      "Comments",
    ];

    const escapeCSV = (val) => {
      const str = String(val ?? "");
      if (str.includes(",") || str.includes('"') || str.includes("\n")) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    const rows = items.map((a) => [
      escapeCSV(a.id),
      escapeCSV(a.title),
      escapeCSV(a.action_item),
      escapeCSV(a.slide_name),
      escapeCSV(a.line_item),
      escapeCSV(a.action_type),
      escapeCSV(a.function_name),
      escapeCSV(a.owner),
      escapeCSV(getDisplayStatus(a)),
      escapeCSV(a.ecd),
      escapeCSV((a.ecd_history || []).join(", ")),
      escapeCSV(a.week),
      escapeCSV(a.createdDate),
      escapeCSV(a.created_by),
      escapeCSV(
        (a.comments || [])
          .map((c) => `[${c.author} ${c.timestamp}] ${c.text}`)
          .join(" | "),
      ),
    ]);

    const csvContent = [headers.join(","), ...rows.map((r) => r.join(","))].join(
      "\n",
    );

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const safeStatus = status.replace(/\s+/g, "_");
    link.href = url;
    link.download = `actions_${safeStatus}_W${week}_${year}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // ─── Row expand ───
  const toggleRow = (id) => {
    setExpandedRow(expandedRow === id ? null : id);
    setEditingDetailId(null);
  };

  // ─── Inline edit (table row) ───
  const startInlineEdit = (action) => {
    setEditingId(action.id);
    // Seed the function selector: if the saved value matches a known report
    // option use the dropdown, otherwise treat it as a custom entry.
    const current = action.function_name || "";
    const isKnown = functionOptions.some((opt) => opt.label === current);
    setEditForm({
      title: action.title,
      owner: action.owner,
      status: action.status,
      ecd: action.ecd,
      function_option: current === "" ? "" : isKnown ? current : "__custom__",
      function_custom: isKnown ? "" : current,
    });
  };

  const cancelInlineEdit = () => {
    setEditingId(null);
    setEditForm({});
  };

  // Save inline edit – if ECD changes, archive the old date into ecd_history
  const saveInlineEdit = (id) => {
    updateActions((prev) =>
      prev.map((a) => {
        if (a.id !== id) return a;

        // Resolve function from the selector, then strip temp fields so they
        // don't get persisted onto the action object.
        const { function_option, function_custom, ...rest } = editForm;
        const functionValue =
          function_option === "__custom__"
            ? (function_custom || "").trim()
            : function_option || "";

        const updatedAction = {
          ...a,
          ...rest,
          function_name: functionValue,
        };

        // If the ECD was changed and the old ECD had a value, archive it
        if (editForm.ecd && editForm.ecd !== a.ecd && a.ecd) {
          const history = a.ecd_history ? [...a.ecd_history] : [];
          history.push(a.ecd);
          updatedAction.ecd_history = history;
        }

        return updatedAction;
      }),
    );
    setEditingId(null);
    setEditForm({});
  };

  // ─── Detail panel edit ───
  const startDetailEdit = (action) => {
    setEditingDetailId(action.id);
    setDetailForm({
      action_item: action.action_item || "",
      slide_name: action.slide_name || "",
      line_item: action.line_item || "",
      action_type: action.action_type || "",
    });
  };

  const cancelDetailEdit = () => {
    setEditingDetailId(null);
    setDetailForm({});
  };

  const saveDetailEdit = (id) => {
    updateActions((prev) =>
      prev.map((a) =>
        a.id === id
          ? {
              ...a,
              action_item: detailForm.action_item,
              slide_name: detailForm.slide_name,
              line_item: detailForm.line_item,
              action_type: detailForm.action_type,
            }
          : a,
      ),
    );
    setEditingDetailId(null);
    setDetailForm({});
  };

  // ─── Comments ───
  const addComment = (actionId) => {
    const text = newCommentText[actionId];
    if (!text || !text.trim()) return;

    const comment = {
      id: `c-${Date.now()}`,
      author: userLogin,
      text: text.trim(),
      timestamp: new Date().toLocaleString("en-US", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      }),
    };

    updateActions((prev) =>
      prev.map((a) =>
        a.id === actionId ? { ...a, comments: [...a.comments, comment] } : a,
      ),
    );
    setNewCommentText((prev) => ({ ...prev, [actionId]: "" }));
  };

  const deleteComment = (actionId, commentId) => {
    updateActions((prev) =>
      prev.map((a) =>
        a.id === actionId
          ? { ...a, comments: a.comments.filter((c) => c.id !== commentId) }
          : a,
      ),
    );
  };

  // ─── New action modal ───
  const openNewModal = () => {
    setModalForm({
      title: "",
      action_item: "",
      slide_name: "",
      line_item: "",
      action_type: "",
      owner: "",
      status: "Open",
      ecd: "",
      function_option: "",      // selected function label, or "__custom__"
      function_custom: "",      // custom text when "Custom" is chosen
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setModalForm({});
  };

  const saveNewAction = () => {
    if (!modalForm.title.trim()) return;

    // Resolve the function: use the custom text when "Custom" is selected,
    // otherwise use the chosen function (sidebar) label.
    const functionValue =
      modalForm.function_option === "__custom__"
        ? (modalForm.function_custom || "").trim()
        : modalForm.function_option || "";

    const newAction = {
      id: `ACT-${String(actions.length + 1).padStart(3, "0")}-${Date.now()}`,
      title: modalForm.title,
      action_item: modalForm.action_item,
      slide_name: modalForm.slide_name,
      line_item: modalForm.line_item,
      action_type: modalForm.action_type,
      owner: modalForm.owner,
      status: modalForm.status,
      function_name: functionValue,
      createdDate: new Date().toISOString().split("T")[0],
      ecd: modalForm.ecd,
      ecd_history: [],
      week: week,
      comments: [],
      created_by: userLogin,
    };

    updateActions((prev) => [newAction, ...prev]);
    closeModal();
  };

  // ─── Delete action ───
  const deleteAction = (id) => {
    updateActions((prev) => prev.filter((a) => a.id !== id));
    if (expandedRow === id) setExpandedRow(null);
    if (editingId === id) cancelInlineEdit();
  };

  // ─── Render ECD with history (strikethrough old dates) ───
  const renderEcdWithHistory = (action) => {
    const history = action.ecd_history || [];
    const currentEcd = action.ecd;

    if (history.length === 0) {
      return (
        <span className={isOverdue(action) ? "at-ecd-overdue" : ""}>
          {currentEcd || "—"}
        </span>
      );
    }

    return (
      <div className="at-ecd-history">
        {history.map((oldDate, idx) => (
          <span key={idx} className="at-ecd-old">
            {oldDate}
          </span>
        ))}
        <span className={`at-ecd-current ${isOverdue(action) ? "at-ecd-overdue" : ""}`}>
          {currentEcd || "—"}
        </span>
      </div>
    );
  };

  // ─── Loading state ───
  if (loading) {
    return (
      <div className="at-container">
        <div className="at-loading">
          <div className="at-loading-spinner"></div>
          <p>Loading action tracker...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="at-container">
      {/* ───── Saving Indicator ───── */}
      {saving && (
        <div className="at-saving-bar">
          <span className="at-saving-dot"></span> Saving...
        </div>
      )}

      {/* ───── Modal Overlay ───── */}
      {showModal && (
        <div className="at-modal-overlay" onClick={closeModal}>
          <div className="at-modal" onClick={(e) => e.stopPropagation()}>
            <div className="at-modal-header">
              <h3 className="at-modal-title">Create New Action</h3>
              <button className="at-modal-close" onClick={closeModal}>
                ✕
              </button>
            </div>
            <div className="at-modal-body">
              <div className="at-modal-grid">
                <div className="at-form-group at-form-group--full">
                  <label className="at-form-label">Title *</label>
                  <input
                    type="text"
                    className="at-input"
                    placeholder="Enter action title..."
                    value={modalForm.title}
                    onChange={(e) =>
                      setModalForm({ ...modalForm, title: e.target.value })
                    }
                  />
                </div>
                <div className="at-form-group at-form-group--full">
                  <label className="at-form-label">Action Item</label>
                  <input
                    type="text"
                    className="at-input"
                    placeholder="Describe the action item..."
                    value={modalForm.action_item}
                    onChange={(e) =>
                      setModalForm({
                        ...modalForm,
                        action_item: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="at-form-group">
                  <label className="at-form-label">Slide Name</label>
                  <input
                    type="text"
                    className="at-input"
                    placeholder="e.g. Slide 4 - Metrics"
                    value={modalForm.slide_name}
                    onChange={(e) =>
                      setModalForm({
                        ...modalForm,
                        slide_name: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="at-form-group">
                  <label className="at-form-label">Line Item</label>
                  <input
                    type="text"
                    className="at-input"
                    placeholder="e.g. Row 3 - Headcount"
                    value={modalForm.line_item}
                    onChange={(e) =>
                      setModalForm({
                        ...modalForm,
                        line_item: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="at-form-group">
                  <label className="at-form-label">Action Type</label>
                  <input
                    type="text"
                    className="at-input"
                    placeholder="e.g. Follow-up, Investigation"
                    value={modalForm.action_type}
                    onChange={(e) =>
                      setModalForm({
                        ...modalForm,
                        action_type: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="at-form-group">
                  <label className="at-form-label">Function</label>
                  <select
                    className="at-select"
                    value={modalForm.function_option}
                    onChange={(e) =>
                      setModalForm({
                        ...modalForm,
                        function_option: e.target.value,
                        // Clear custom text when switching away from Custom
                        function_custom:
                          e.target.value === "__custom__"
                            ? modalForm.function_custom
                            : "",
                      })
                    }
                  >
                    <option value="">— Select function —</option>
                    {functionOptions.map((opt) => (
                      <option key={opt.key} value={opt.label}>
                        {opt.label}
                      </option>
                    ))}
                    <option value="__custom__">Custom…</option>
                  </select>
                </div>
                {modalForm.function_option === "__custom__" && (
                  <div className="at-form-group">
                    <label className="at-form-label">Custom Function Name</label>
                    <input
                      type="text"
                      className="at-input"
                      placeholder="Type a custom function name..."
                      value={modalForm.function_custom}
                      onChange={(e) =>
                        setModalForm({
                          ...modalForm,
                          function_custom: e.target.value,
                        })
                      }
                    />
                  </div>
                )}
                <div className="at-form-group">
                  <label className="at-form-label">Owner (Alias)</label>
                  <input
                    type="text"
                    className="at-input"
                    placeholder="e.g. jdoe"
                    value={modalForm.owner}
                    onChange={(e) =>
                      setModalForm({ ...modalForm, owner: e.target.value })
                    }
                  />
                </div>
                <div className="at-form-group">
                  <label className="at-form-label">Status</label>
                  <select
                    className="at-select"
                    value={modalForm.status}
                    onChange={(e) =>
                      setModalForm({ ...modalForm, status: e.target.value })
                    }
                  >
                    <option value="Open">Open</option>
                    <option value="In Progress">In Progress</option>
                    <option value="Completed">Completed</option>
                    <option value="Overdue">Overdue</option>
                    <option value="Blocked">Blocked</option>
                  </select>
                </div>
                <div className="at-form-group">
                  <label className="at-form-label">ECD</label>
                  <input
                    type="date"
                    className="at-input"
                    value={modalForm.ecd}
                    onChange={(e) =>
                      setModalForm({ ...modalForm, ecd: e.target.value })
                    }
                  />
                </div>
              </div>
            </div>
            <div className="at-modal-footer">
              <button className="at-btn at-btn--secondary" onClick={closeModal}>
                Cancel
              </button>
              <button
                className="at-btn at-btn--primary"
                onClick={saveNewAction}
                disabled={!modalForm.title?.trim()}
              >
                Create Action
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ───── Header ───── */}
      <div className="at-header">
        <div className="at-header-left">
          <h1 className="at-title">Action Tracker</h1>
          <p className="at-subtitle">
            Week {week}, {year} &middot; Tracking {stats.total} action items
          </p>
        </div>
        <div className="at-header-right">
          <button className="at-btn at-btn--primary" onClick={openNewModal}>
            <span className="at-btn-icon">+</span> New Action
          </button>
          <button
            className="at-btn at-btn--secondary"
            onClick={loadActions}
            disabled={loading}
          >
            <span className="at-btn-icon">↻</span> Refresh
          </button>
        </div>
      </div>

      {/* ───── Stats with Export Icons ───── */}
      <div className="at-stats-grid">
        <div className={`at-stat-card ${statusFilter === "All" ? "at-stat-card--active" : ""}`} onClick={() => setStatusFilter("All")} style={{ cursor: "pointer" }}>
          <div className="at-stat-card-header">
            <span className="at-stat-label">Total</span>
            <button
              className="at-stat-export-btn"
              onClick={(e) => { e.stopPropagation(); exportByStatus("All"); }}
              title="Export all actions as CSV"
              disabled={stats.total === 0}
            >
              <svg
                className="at-stat-export-icon"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path d="M10 3a1 1 0 01 1 1v7.586l2.293-2.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 11.586V4a1 1 0 011-1z" />
                <path d="M4 14a1 1 0 011 1v1h10v-1a1 1 0 112 0v2a1 1 0 01-1 1H4a1 1 0 01-1-1v-2a1 1 0 011-1z" />
              </svg>
            </button>
          </div>
          <span className="at-stat-value">{stats.total}</span>
          <span className="at-stat-sub">All items</span>
        </div>

        <div className={`at-stat-card ${statusFilter === "Open" ? "at-stat-card--active" : ""}`} onClick={() => setStatusFilter("Open")} style={{ cursor: "pointer" }}>
          <div className="at-stat-card-header">
            <span className="at-stat-label">Open</span>
            <button
              className="at-stat-export-btn at-stat-export-btn--open"
              onClick={(e) => { e.stopPropagation(); exportByStatus("Open"); }}
              title="Export open actions as CSV"
              disabled={stats.open === 0}
            >
              <svg
                className="at-stat-export-icon"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path d="M10 3a1 1 0 01 1 1v7.586l2.293-2.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 11.586V4a1 1 0 011-1z" />
                <path d="M4 14a1 1 0 011 1v1h10v-1a1 1 0 112 0v2a1 1 0 01-1 1H4a1 1 0 01-1-1v-2a1 1 0 011-1z" />
              </svg>
            </button>
          </div>
          <span className="at-stat-value at-stat-value--open">
            {stats.open}
          </span>
          <span className="at-stat-sub">Awaiting action</span>
        </div>

        <div className={`at-stat-card ${statusFilter === "In Progress" ? "at-stat-card--active" : ""}`} onClick={() => setStatusFilter("In Progress")} style={{ cursor: "pointer" }}>
          <div className="at-stat-card-header">
            <span className="at-stat-label">In Progress</span>
            <button
              className="at-stat-export-btn at-stat-export-btn--progress"
              onClick={(e) => { e.stopPropagation(); exportByStatus("In Progress"); }}
              title="Export in-progress actions as CSV"
              disabled={stats.inProgress === 0}
            >
              <svg
                className="at-stat-export-icon"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path d="M10 3a1 1 0 01 1 1v7.586l2.293-2.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 11.586V4a1 1 0 011-1z" />
                <path d="M4 14a1 1 0 011 1v1h10v-1a1 1 0 112 0v2a1 1 0 01-1 1H4a1 1 0 01-1-1v-2a1 1 0 011-1z" />
              </svg>
            </button>
          </div>
          <span className="at-stat-value at-stat-value--progress">
            {stats.inProgress}
          </span>
          <span className="at-stat-sub">Being worked on</span>
        </div>

        <div className={`at-stat-card ${statusFilter === "Completed" ? "at-stat-card--active" : ""}`} onClick={() => setStatusFilter("Completed")} style={{ cursor: "pointer" }}>
          <div className="at-stat-card-header">
            <span className="at-stat-label">Completed</span>
            <button
              className="at-stat-export-btn at-stat-export-btn--completed"
              onClick={(e) => { e.stopPropagation(); exportByStatus("Completed"); }}
              title="Export completed actions as CSV"
              disabled={stats.completed === 0}
            >
              <svg
                className="at-stat-export-icon"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path d="M10 3a1 1 0 01 1 1v7.586l2.293-2.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 11.586V4a1 1 0 011-1z" />
                <path d="M4 14a1 1 0 011 1v1h10v-1a1 1 0 112 0v2a1 1 0 01-1 1H4a1 1 0 01-1-1v-2a1 1 0 011-1z" />
              </svg>
            </button>
          </div>
          <span className="at-stat-value at-stat-value--completed">
            {stats.completed}
          </span>
          <span className="at-stat-sub">Done</span>
        </div>

        <div className={`at-stat-card ${statusFilter === "Overdue" ? "at-stat-card--active" : ""}`} onClick={() => setStatusFilter("Overdue")} style={{ cursor: "pointer" }}>
          <div className="at-stat-card-header">
            <span className="at-stat-label">Overdue</span>
            <button
              className="at-stat-export-btn at-stat-export-btn--overdue"
              onClick={(e) => { e.stopPropagation(); exportByStatus("Overdue"); }}
              title="Export overdue actions as CSV"
              disabled={stats.overdue === 0}
            >
              <svg
                className="at-stat-export-icon"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path d="M10 3a1 1 0 01 1 1v7.586l2.293-2.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 11.586V4a1 1 0 011-1z" />
                <path d="M4 14a1 1 0 011 1v1h10v-1a1 1 0 112 0v2a1 1 0 01-1 1H4a1 1 0 01-1-1v-2a1 1 0 011-1z" />
              </svg>
            </button>
          </div>
          <span className="at-stat-value at-stat-value--overdue">
            {stats.overdue}
          </span>
          <span className="at-stat-sub">Past ECD</span>
        </div>

        <div className={`at-stat-card ${statusFilter === "Blocked" ? "at-stat-card--active" : ""}`} onClick={() => setStatusFilter("Blocked")} style={{ cursor: "pointer" }}>
          <div className="at-stat-card-header">
            <span className="at-stat-label">Blocked</span>
            <button
              className="at-stat-export-btn at-stat-export-btn--blocked"
              onClick={(e) => { e.stopPropagation(); exportByStatus("Blocked"); }}
              title="Export blocked actions as CSV"
              disabled={stats.blocked === 0}
            >
              <svg
                className="at-stat-export-icon"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path d="M10 3a1 1 0 01 1 1v7.586l2.293-2.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 11.586V4a1 1 0 011-1z" />
                <path d="M4 14a1 1 0 011 1v1h10v-1a1 1 0 112 0v2a1 1 0 01-1 1H4a1 1 0 01-1-1v-2a1 1 0 011-1z" />
              </svg>
            </button>
          </div>
          <span className="at-stat-value at-stat-value--blocked">
            {stats.blocked}
          </span>
          <span className="at-stat-sub">Needs unblocking</span>
        </div>
      </div>

      {/* ───── Filters ───── */}
      <div className="at-card">
        <div className="at-filters">
          <div className="at-filter-group">
            <label className="at-filter-label">Search</label>
            <input
              type="text"
              className="at-input"
              placeholder="Search by ID, title, owner, slide, line item..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="at-filter-group">
            <label className="at-filter-label">Status</label>
            <select
              className="at-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              {STATUS_OPTIONS.map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </select>
          </div>
          <div className="at-filter-group at-filter-group--count">
            <span className="at-result-count">
              {filteredActions.length} of {actions.length} items
            </span>
          </div>
        </div>
      </div>

      {/* ───── Table ───── */}
      <div className="at-card">
        <div className="at-table-responsive">
          {filteredActions.length === 0 ? (
            <div className="at-empty">
              <span className="at-empty-icon">📋</span>
              <p className="at-empty-text">No actions match your filters</p>
              <button
                className="at-btn at-btn--secondary"
                onClick={() => {
                  setSearchTerm("");
                  setStatusFilter("All");
                }}
              >
                Clear Filters
              </button>
            </div>
          ) : (
            <table className="at-table">
              <thead>
                <tr>
                  <th className="at-th">ID</th>
                  <th className="at-th">Function</th>
                  <th className="at-th at-th--title">Title</th>
                  <th className="at-th">Owner</th>
                  <th className="at-th">Status</th>
                  <th className="at-th">ECD</th>
                  <th className="at-th">Week</th>
                  <th className="at-th at-th--actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredActions.map((action) => (
                  <React.Fragment key={action.id}>
                    <tr
                      className={`at-table-row ${
                        editingId === action.id ? "at-table-row--editing" : ""
                      } ${isOverdue(action) ? "at-table-row--overdue" : ""}`}
                    >
                      <td className="at-td at-td--id" title={action.id}>
                        {getShortId(action.id)}
                      </td>
                      <td className="at-td at-td--function">
                        {editingId === action.id ? (
                          <div className="at-inline-function">
                            <select
                              className="at-inline-select"
                              value={editForm.function_option}
                              onChange={(e) =>
                                setEditForm({
                                  ...editForm,
                                  function_option: e.target.value,
                                  function_custom:
                                    e.target.value === "__custom__"
                                      ? editForm.function_custom
                                      : "",
                                })
                              }
                            >
                              <option value="">— None —</option>
                              {functionOptions.map((opt) => (
                                <option key={opt.key} value={opt.label}>
                                  {opt.label}
                                </option>
                              ))}
                              <option value="__custom__">Custom…</option>
                            </select>
                            {editForm.function_option === "__custom__" && (
                              <input
                                type="text"
                                className="at-inline-input"
                                placeholder="Custom function name..."
                                value={editForm.function_custom}
                                onChange={(e) =>
                                  setEditForm({
                                    ...editForm,
                                    function_custom: e.target.value,
                                  })
                                }
                              />
                            )}
                          </div>
                        ) : (
                          action.function_name || "—"
                        )}
                      </td>
                      <td className="at-td at-td--title">
                        {editingId === action.id ? (
                          <input
                            type="text"
                            className="at-inline-input"
                            value={editForm.title}
                            onChange={(e) =>
                              setEditForm({
                                ...editForm,
                                title: e.target.value,
                              })
                            }
                          />
                        ) : (
                          action.title
                        )}
                      </td>
                      <td className="at-td">
                        {editingId === action.id ? (
                          <input
                            type="text"
                            className="at-inline-input"
                            placeholder="alias"
                            value={editForm.owner}
                            onChange={(e) =>
                              setEditForm({
                                ...editForm,
                                owner: e.target.value,
                              })
                            }
                          />
                        ) : (
                          <div className="at-person">
                            <span className="at-avatar">
                              {action.owner
                                ? action.owner.charAt(0).toUpperCase()
                                : "?"}
                            </span>
                            <span className="at-person-alias">
                              @{action.owner}
                            </span>
                          </div>
                        )}
                      </td>
                      <td className="at-td">
                        {editingId === action.id ? (
                          <select
                            className="at-inline-select"
                            value={editForm.status}
                            onChange={(e) =>
                              setEditForm({
                                ...editForm,
                                status: e.target.value,
                              })
                            }
                          >
                            <option value="Open">Open</option>
                            <option value="In Progress">In Progress</option>
                            <option value="Completed">Completed</option>
                            <option value="Overdue">Overdue</option>
                            <option value="Blocked">Blocked</option>
                          </select>
                        ) : (
                          <span
                            className={`at-status-badge ${getStatusClass(
                              getDisplayStatus(action),
                            )}`}
                          >
                            {getDisplayStatus(action)}
                          </span>
                        )}
                      </td>
                      <td className="at-td at-td--date">
                        {editingId === action.id ? (
                          <input
                            type="date"
                            className="at-inline-input"
                            value={editForm.ecd}
                            onChange={(e) =>
                              setEditForm({
                                ...editForm,
                                ecd: e.target.value,
                              })
                            }
                          />
                        ) : (
                          renderEcdWithHistory(action)
                        )}
                      </td>
                      <td className="at-td at-td--week">
                        {action.week || "—"}
                      </td>
                      <td className="at-td at-td--actions">
                        <div className="at-action-btns">
                          {editingId === action.id ? (
                            <>
                              <button
                                className="at-btn-sm at-btn-sm--save"
                                onClick={() => saveInlineEdit(action.id)}
                                title="Save"
                              >
                                ✓
                              </button>
                              <button
                                className="at-btn-sm at-btn-sm--cancel"
                                onClick={cancelInlineEdit}
                                title="Cancel"
                              >
                                ✕
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                className="at-btn-sm at-btn-sm--edit"
                                onClick={() => startInlineEdit(action)}
                                title="Edit row"
                              >
                                ✎
                              </button>
                              <button
                                className="at-btn-sm at-btn-sm--expand"
                                onClick={() => toggleRow(action.id)}
                                title="Toggle details"
                              >
                                {expandedRow === action.id ? "▲" : "▼"}
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>

                    {expandedRow === action.id && (
                      <tr className="at-expanded-row">
                        <td colSpan="8">
                          <div className="at-expanded-content">
                            <div className="at-expanded-section">
                              <div className="at-expanded-section-header">
                                <h4 className="at-expanded-section-title">
                                  Details
                                </h4>
                                {editingDetailId === action.id ? (
                                  <div className="at-expanded-section-actions">
                                    <button
                                      className="at-btn-sm at-btn-sm--save"
                                      onClick={() => saveDetailEdit(action.id)}
                                    >
                                      ✓ Save
                                    </button>
                                    <button
                                      className="at-btn-sm at-btn-sm--cancel"
                                      onClick={cancelDetailEdit}
                                    >
                                      ✕ Cancel
                                    </button>
                                  </div>
                                ) : (
                                  <button
                                    className="at-btn-sm at-btn-sm--edit"
                                    onClick={() => startDetailEdit(action)}
                                  >
                                    ✎ Edit Details
                                  </button>
                                )}
                              </div>
                              <div className="at-expanded-grid">
                                <div className="at-expanded-item at-expanded-item--full">
                                  <span className="at-expanded-label">
                                    Action Item
                                  </span>
                                  {editingDetailId === action.id ? (
                                    <input
                                      type="text"
                                      className="at-inline-input"
                                      value={detailForm.action_item}
                                      onChange={(e) =>
                                        setDetailForm({
                                          ...detailForm,
                                          action_item: e.target.value,
                                        })
                                      }
                                    />
                                  ) : (
                                    <span className="at-expanded-value">
                                      {action.action_item || "—"}
                                    </span>
                                  )}
                                </div>
                                <div className="at-expanded-item">
                                  <span className="at-expanded-label">
                                    Slide Name
                                  </span>
                                  {editingDetailId === action.id ? (
                                    <input
                                      type="text"
                                      className="at-inline-input"
                                      value={detailForm.slide_name}
                                      onChange={(e) =>
                                        setDetailForm({
                                          ...detailForm,
                                          slide_name: e.target.value,
                                        })
                                      }
                                    />
                                  ) : (
                                    <span className="at-expanded-value">
                                      {action.slide_name || "—"}
                                    </span>
                                  )}
                                </div>
                                <div className="at-expanded-item">
                                  <span className="at-expanded-label">
                                    Line Item
                                  </span>
                                  {editingDetailId === action.id ? (
                                    <input
                                      type="text"
                                      className="at-inline-input"
                                      value={detailForm.line_item}
                                      onChange={(e) =>
                                        setDetailForm({
                                          ...detailForm,
                                          line_item: e.target.value,
                                        })
                                      }
                                    />
                                  ) : (
                                    <span className="at-expanded-value">
                                      {action.line_item || "—"}
                                    </span>
                                  )}
                                </div>
                                <div className="at-expanded-item">
                                  <span className="at-expanded-label">
                                    Action Type
                                  </span>
                                  {editingDetailId === action.id ? (
                                    <input
                                      type="text"
                                      className="at-inline-input"
                                      value={detailForm.action_type}
                                      onChange={(e) =>
                                        setDetailForm({
                                          ...detailForm,
                                          action_type: e.target.value,
                                        })
                                      }
                                    />
                                  ) : (
                                    <span className="at-expanded-value">
                                      {action.action_type || "—"}
                                    </span>
                                  )}
                                </div>
                                <div className="at-expanded-item">
                                  <span className="at-expanded-label">
                                    Function
                                  </span>
                                  <span className="at-expanded-value">
                                    {action.function_name || "—"}
                                  </span>
                                </div>
                                <div className="at-expanded-item">
                                  <span className="at-expanded-label">
                                    Created Date
                                  </span>
                                  <span className="at-expanded-value">
                                    {action.createdDate || "—"}
                                  </span>
                                </div>
                                <div className="at-expanded-item">
                                  <span className="at-expanded-label">
                                    Created By
                                  </span>
                                  <span className="at-expanded-value">
                                    {action.created_by ? (
                                      <div className="at-person">
                                        <span className="at-avatar">
                                          {action.created_by
                                            .charAt(0)
                                            .toUpperCase()}
                                        </span>
                                        <span className="at-person-alias">
                                          @{action.created_by}
                                        </span>
                                      </div>
                                    ) : (
                                      "—"
                                    )}
                                  </span>
                                </div>
                              </div>
                            </div>

                            <div className="at-expanded-section at-comments-section">
                              <div className="at-expanded-section-header">
                                <h4 className="at-expanded-section-title">
                                  Comments ({action.comments.length})
                                </h4>
                              </div>
                              <div className="at-comments-list">
                                {action.comments.length === 0 ? (
                                  <p className="at-comments-empty">
                                    No comments yet. Be the first to comment.
                                  </p>
                                ) : (
                                  action.comments.map((comment) => (
                                    <div
                                      key={comment.id}
                                      className="at-comment"
                                    >
                                      <div className="at-comment-header">
                                        <div className="at-comment-author">
                                          <span className="at-comment-avatar">
                                            {comment.author.charAt(0)}
                                          </span>
                                          <div>
                                            <span className="at-comment-name">
                                              {comment.author}
                                            </span>
                                          </div>
                                        </div>
                                        <div className="at-comment-meta">
                                          <span className="at-comment-time">
                                            {comment.timestamp}
                                          </span>
                                          <button
                                            className="at-btn-sm at-btn-sm--delete"
                                            onClick={() =>
                                              deleteComment(
                                                action.id,
                                                comment.id,
                                              )
                                            }
                                            title="Delete comment"
                                          >
                                            ✕
                                          </button>
                                        </div>
                                      </div>
                                      <p className="at-comment-text">
                                        {comment.text}
                                      </p>
                                    </div>
                                  ))
                                )}
                              </div>
                              <div className="at-comment-add">
                                <div className="at-comment-input-row">
                                  <span className="at-comment-avatar at-comment-avatar--self">
                                    C
                                  </span>
                                  <textarea
                                    className="at-textarea at-comment-textarea"
                                    rows="2"
                                    placeholder="Write a comment..."
                                    value={newCommentText[action.id] || ""}
                                    onChange={(e) =>
                                      setNewCommentText((prev) => ({
                                        ...prev,
                                        [action.id]: e.target.value,
                                      }))
                                    }
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter" && e.ctrlKey) {
                                        addComment(action.id);
                                      }
                                    }}
                                  />
                                </div>
                                <div className="at-comment-submit-row">
                                  <span className="at-comment-hint">
                                    Ctrl + Enter to submit
                                  </span>
                                  <button
                                    className="at-btn at-btn--primary at-btn--sm"
                                    onClick={() => addComment(action.id)}
                                    disabled={
                                      !newCommentText[action.id]?.trim()
                                    }
                                  >
                                    Add Comment
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* ───── Footer ───── */}
      <div className="at-footer-bar">
        Showing {filteredActions.length} of {actions.length} action items
      </div>
    </div>
  );
}
